import React from 'react';
import { Link } from 'react-router-dom';
import CosmicHero from '../components/CosmicHero';
import Seo, { SITE_URL } from '../components/Seo';
import { blogPosts } from '../services/blogData';
import styles from './Home.module.css';

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'VastuLens',
  url: SITE_URL,
  logo: `${SITE_URL}/logo.jpg`,
  description:
    'AI-powered Vastu Shastra analysis platform. Upload a floor plan and get a 16-zone Vastu report personalised to your birth chart.',
};

const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'VastuLens',
  url: SITE_URL,
};

const steps = [
  {
    title: 'Upload your floor plan',
    text: 'Any clear image of your home layout works — a builder plan, a brochure page, or a neat sketch.',
  },
  {
    title: 'Align north',
    text: 'Rotate the compass to match your plan so every direction is measured from your true north.',
  },
  {
    title: 'Verify detected rooms',
    text: 'Our AI reads the plan and maps each room onto the 16-zone Vastu grid. You review and correct anything before scoring.',
  },
  {
    title: 'Get your personalised report',
    text: 'Room-by-room scores, severity levels, and practical remedies — tuned to your birth chart if you share your birth details.',
  },
];

function Home() {
  const featuredPosts = blogPosts.slice(0, 3);

  return (
    <div>
      <Seo
        title="VastuLens — AI Vastu Analysis of Your Floor Plan Online"
        description="Upload your floor plan and get an instant AI-powered Vastu Shastra analysis: 16-zone mapping, room-by-room scores, birth-chart personalisation and practical remedies."
        path="/"
        jsonLd={[organizationSchema, websiteSchema]}
      />

      <CosmicHero />

      {/* Crawlable content below the scroll experience */}
      <section className={styles.seoSection}>
        <div className="container">
          <h2 className={styles.seoHeading}>
            AI-Powered <em>Vastu Analysis</em> for Your Home
          </h2>
          <p className={styles.seoLead}>
            VastuLens reads your floor plan the way a Vastu consultant would — and shows its
            working. Computer vision finds your home&apos;s centre (the Brahmasthan) and overlays
            the classical 16-zone Vastu Purusha Mandala on your actual layout. Every room is then
            scored against documented Vastu rules, room by room and zone by zone, so you see not
            just <em>what</em> to fix but <em>why</em>.
          </p>
          <p className={styles.seoLead}>
            Unlike generic direction charts, VastuLens can personalise your report with
            astro-Vastu: your sidereal birth chart highlights which zones matter most for you,
            and remedies are ranked accordingly. The full scoring method is public on our{' '}
            <Link to="/methodology" className={styles.seoLink}>methodology page</Link>.
          </p>

          <h2 className={styles.seoHeading}>How It Works</h2>
          <div className={styles.stepsGrid}>
            {steps.map((step, i) => (
              <div key={step.title} className={styles.stepCard}>
                <div className={styles.stepNumber}>{i + 1}</div>
                <h3 className={styles.stepTitle}>{step.title}</h3>
                <p className={styles.stepText}>{step.text}</p>
              </div>
            ))}
          </div>

          <div className={styles.seoCtaRow}>
            <Link to="/analysis" className={styles.seoCtaPrimary}>
              Analyze Your Floor Plan
            </Link>
            <Link to="/compare" className={styles.seoCtaSecondary}>
              Compare Two Properties
            </Link>
          </div>

          <h2 className={styles.seoHeading}>From the Knowledge Base</h2>
          <ul className={styles.postList}>
            {featuredPosts.map((post) => (
              <li key={post.slug} className={styles.postItem}>
                <Link to={`/blog/${post.slug}`} className={styles.postLink}>
                  {post.title}
                </Link>
                <p className={styles.postExcerpt}>{post.excerpt}</p>
              </li>
            ))}
          </ul>
          <p className={styles.seoLead}>
            <Link to="/blog" className={styles.seoLink}>Browse all Vastu guides →</Link>
          </p>
        </div>
      </section>
    </div>
  );
}

export default Home;
